<script lang="ts">
  import { onMount } from "svelte"
  import type { OrganizationAddressSettings } from "@kontextwerk/control-web-client"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import GhostButton from "@kontextwerk/control-web-frame/shared/modules/ghost-button"
  import { validateAvailabilityPolicyConfig } from "@kontextwerk/control-web-frame/shared/logic/availability-policies"
  import BrandingSection from "@kontextwerk/control-web-frame/shared/modules/branding-section"
  import type { AvailabilityPolicyConfig } from "@kontextwerk/control-web-client"
  import {
    fetchOrganizationBusinessProfile,
    updateOrganizationBusinessProfile,
    type KehrblattBusinessProfile,
    type KehrblattBusinessProfileUpdate,
  } from "../../../../../../../../api/kehrblatt/business-profile"
  import {
    DACH_COUNTRY_OPTIONS,
    normalizeDachCountrySelection,
  } from "../../../../../../../../_shared/_logic/countries"
  import { normalizeOrganizationTimezone } from "../../../../../../../_shared/_logic/timezones"
  import DemoDataSection from "./_modules/demo-data-section"
  import { canResetDemoData } from "../../../../../../_shared/_logic/access"
  import type { PageContext } from "../../context"
  import ChatWidgetBox from "./_modules/chat-widget-box"
  import ServiceHoursEditorOverlay from "./_modules/service-hours-editor-overlay"
  import ServiceHoursPreviewBox from "./_modules/service-hours-preview-box"

  type PresentationIndexingSelection = "disabled" | "enabled"

  const PRESENTATION_INDEXING_OPTIONS = [
    { name: "Nicht indexieren (empfohlen)", value: "disabled" },
    { name: "Indexierung erlauben", value: "enabled" },
  ]

  type BusinessProfileForm = {
    organization_name: string
    public_name: string
    data_privacy_url: string
    contact_email: string
    contact_phone: string
    public_contact_email: string
    public_contact_phone: string
    presentation_indexing: PresentationIndexingSelection
    address: {
      street: string
      house_number: string
      address_extra: string
      zip: string
      city: string
      country: string
    }
    timezone: string
    timezone_source: "country" | "manual"
    service_hours: AvailabilityPolicyConfig | null
  }

  let { pageContext: pc, showChatWidget = false } = $props<{
    pageContext: PageContext
    showChatWidget?: boolean
  }>()

  const canManageOrgSettings = $derived(pc.permissions.allows("core.org_settings", "manage"))
  const organizationId = $derived(pc.organizationId ?? pc.organization?.id ?? null)

  let form = $state<BusinessProfileForm | null>(null)
  let lastSavedProfile = $state<KehrblattBusinessProfile | null>(null)
  let loading = $state(false)
  let saving = $state(false)
  let error = $state<string | null>(null)

  const text = (value: string | null | undefined): string => value?.trim() ?? ""
  const presentationIndexingSelection = (
    enabled: boolean | null | undefined,
  ): PresentationIndexingSelection => (enabled === true ? "enabled" : "disabled")
  const presentationIndexingEnabled = (selection: PresentationIndexingSelection): boolean =>
    selection === "enabled"

  const toForm = (profile: KehrblattBusinessProfile): BusinessProfileForm => ({
    organization_name: text(profile.organization_name),
    public_name: text(profile.public_name),
    data_privacy_url: text(profile.data_privacy_url),
    contact_email: text(profile.contact_email),
    contact_phone: text(profile.contact_phone),
    public_contact_email: text(profile.public_contact_email),
    public_contact_phone: text(profile.public_contact_phone),
    presentation_indexing: presentationIndexingSelection(profile.presentation_indexing_enabled),
    address: {
      street: text(profile.address?.street),
      house_number: text(profile.address?.house_number),
      address_extra: text(profile.address?.address_extra),
      zip: text(profile.address?.zip),
      city: text(profile.address?.city),
      country: normalizeDachCountrySelection(profile.address?.country),
    },
    timezone: normalizeOrganizationTimezone(profile.timezone, profile.address?.country),
    timezone_source: profile.timezone_source === "manual" ? "manual" : "country",
    service_hours: profile.service_hours ?? null,
  })

  const normalizedAddress = (
    address: BusinessProfileForm["address"],
  ): OrganizationAddressSettings => ({
    street: text(address.street) || null,
    house_number: text(address.house_number) || null,
    address_extra: text(address.address_extra) || null,
    zip: text(address.zip) || null,
    city: text(address.city) || null,
    country: normalizeDachCountrySelection(address.country),
  })

  const displayedTimezone = (value: BusinessProfileForm): string =>
    value.timezone_source === "country"
      ? normalizeOrganizationTimezone(null, value.address.country)
      : normalizeOrganizationTimezone(value.timezone, value.address.country)

  const toUpdatePayload = (value: BusinessProfileForm): KehrblattBusinessProfileUpdate => ({
    public_name: text(value.public_name) || null,
    data_privacy_url: text(value.data_privacy_url) || null,
    contact_email: text(value.contact_email),
    contact_phone: text(value.contact_phone),
    public_contact_email: text(value.public_contact_email) || null,
    public_contact_phone: text(value.public_contact_phone) || null,
    presentation_indexing_enabled: presentationIndexingEnabled(value.presentation_indexing),
    address: normalizedAddress(value.address),
    service_hours: value.service_hours,
  })

  const loadBusinessProfile = async (targetOrganizationId: string) => {
    if (loading) return

    loading = true
    error = null
    try {
      const response = await fetchOrganizationBusinessProfile(targetOrganizationId)
      lastSavedProfile = response.profile
      form = toForm(response.profile)
    } catch (err) {
      console.error("kehrblatt_business_profile_load_failed", err)
      error = "Organisationseinstellungen konnten nicht geladen werden."
    } finally {
      loading = false
    }
  }

  onMount(() => {
    if (organizationId) {
      void loadBusinessProfile(organizationId)
    }
  })

  const saveBusinessProfile = async () => {
    if (!organizationId || !form || !canManageOrgSettings || saving) return

    const contactEmail = text(form.contact_email)
    const contactPhone = text(form.contact_phone)

    if (!contactEmail || !contactPhone) {
      newNotification({ class: "error", html: "E-Mail und Telefonnummer sind erforderlich." })

      return
    }

    const serviceHoursError = validateAvailabilityPolicyConfig(form.service_hours)

    if (serviceHoursError) {
      newNotification({ class: "error", html: serviceHoursError })

      return
    }

    saving = true
    error = null
    try {
      const response = await updateOrganizationBusinessProfile(
        organizationId,
        toUpdatePayload(form),
      )
      lastSavedProfile = response.profile
      form = toForm(response.profile)
      pc.onOrganizationUpdated(new CustomEvent("updated", { detail: response.organization }))
      newNotification({ class: "success", html: "Angaben gespeichert." })
    } catch (err) {
      console.error("kehrblatt_business_profile_save_failed", err)
      error = "Organisationseinstellungen konnten nicht gespeichert werden."
    } finally {
      saving = false
    }
  }

  const saveServiceHours = async (serviceHours: AvailabilityPolicyConfig | null) => {
    if (!organizationId || !form || !lastSavedProfile || !canManageOrgSettings || saving) {
      throw new Error("service_hours_save_unavailable")
    }

    const baseForm = toForm(lastSavedProfile)
    const response = await updateOrganizationBusinessProfile(organizationId, {
      ...toUpdatePayload(baseForm),
      service_hours: serviceHours,
    })
    lastSavedProfile = response.profile
    form.service_hours = response.profile.service_hours ?? null
    pc.onOrganizationUpdated(new CustomEvent("updated", { detail: response.organization }))
  }

  const openServiceHoursOverlay = () => {
    if (!organizationId || !form) return

    openOverlay({
      title: "Servicezeiten bearbeiten",
      content: ServiceHoursEditorOverlay,
      isDark: false,
      properties: {
        organizationId,
        value: form.service_hours,
        disabled: saving || !canManageOrgSettings,
        onSave: saveServiceHours,
      },
    })
  }
</script>

{#if error}
  <div class="state-block">
    <p>{error}</p>
    {#if organizationId}
      <GhostButton
        onclick={() => {
          void loadBusinessProfile(organizationId)
        }}
      >
        Erneut versuchen
      </GhostButton>
    {/if}
  </div>
{/if}

{#if (!organizationId || loading) && !form}
  <div class="state-block">
    <p>Organisationseinstellungen werden geladen...</p>
  </div>
{:else if organizationId && form}
  <div class="form" data-testid="kehrblatt.organizationSettings.generalTab">
    <section class="settings-section">
      <SectionTitle
        title="Kritische Informationen"
        small={true}
        brightBackground={true}
        helpModalTextId="organizationCriticalInformation"
      >
        {#snippet buttonSnippet()}
          <CTA
            type="primaryButton"
            content="Speichern"
            onClick={saveBusinessProfile}
            disabled={saving || loading || !canManageOrgSettings}
            small={true}
          />
        {/snippet}
      </SectionTitle>

      <ActionBox label="Kritische Informationen">
        <div class="section-fields">
          <div class="row">
            <Input
              placeholder="Organisationsname"
              bind:value={form.organization_name}
              disabled={true}
            />
            <Input
              placeholder="Öffentlicher Name"
              bind:value={form.public_name}
              tooltip="Unter diesem Namen stellt sich der KI-Agent vor."
              disabled={saving || !canManageOrgSettings}
            />
          </div>
          <div class="row">
            <Input
              placeholder="Datenschutz-Link (https://…/datenschutz)"
              bind:value={form.data_privacy_url}
              tooltip="Link zur Datenschutzerklärung für Datenschutz-Hinweise und Einwilligungen."
              disabled={saving || !canManageOrgSettings}
            />
          </div>
          <div class="row">
            <Input
              placeholder="E-Mail"
              bind:value={form.contact_email}
              tooltip="Diese E-Mail-Adresse nutzt die KI für Eskalationen und interne Rückfragen."
              required={true}
              disabled={saving || !canManageOrgSettings}
            />
            <Input
              placeholder="Telefonnummer (+491701234567)"
              bind:value={form.contact_phone}
              tooltip="Diese Telefonnummer nutzt die KI für Eskalationen und interne Rückfragen."
              required={true}
              disabled={saving || !canManageOrgSettings}
            />
          </div>
        </div>
      </ActionBox>
    </section>

    <section class="settings-section">
      <SectionTitle
        title="Öffentliche Informationen"
        small={true}
        brightBackground={true}
        helpModalTextId="organizationPublicInformation"
      >
        {#snippet buttonSnippet()}
          <CTA
            type="primaryButton"
            content="Speichern"
            onClick={saveBusinessProfile}
            disabled={saving || loading || !canManageOrgSettings}
            small={true}
          />
        {/snippet}
      </SectionTitle>

      <ActionBox label="Öffentliche Informationen">
        <div class="section-fields">
          <div class="row row--triple">
            <Input
              placeholder="Straße"
              bind:value={form.address.street}
              disabled={saving || !canManageOrgSettings}
            />
            <Input
              placeholder="Hausnummer"
              bind:value={form.address.house_number}
              disabled={saving || !canManageOrgSettings}
            />
            <Input
              placeholder="Adresszusatz"
              bind:value={form.address.address_extra}
              disabled={saving || !canManageOrgSettings}
            />
          </div>
          <div class="row row--triple">
            <Input
              placeholder="PLZ"
              bind:value={form.address.zip}
              disabled={saving || !canManageOrgSettings}
            />
            <Input
              placeholder="Ort"
              bind:value={form.address.city}
              disabled={saving || !canManageOrgSettings}
            />
            <Input
              placeholder="Land"
              type="select"
              options={DACH_COUNTRY_OPTIONS}
              bind:value={form.address.country}
              brightBackground={true}
              disabled={saving || !canManageOrgSettings}
            />
          </div>
          <div class="row">
            <Input placeholder="Zeitzone" value={displayedTimezone(form)} disabled={true} />
            <Input
              placeholder="Suchmaschinen"
              type="select"
              options={PRESENTATION_INDEXING_OPTIONS}
              bind:value={form.presentation_indexing}
              brightBackground={true}
              tooltip="Steuert, ob öffentliche Profil- und Betriebsseiten von Suchmaschinen indexiert werden dürfen. Persönliche Terminlinks, Datenschutz und Kontaktwege bleiben noindex."
              disabled={saving || !canManageOrgSettings}
            />
          </div>
          <div class="row">
            <Input
              placeholder="Öffentliche E-Mail"
              bind:value={form.public_contact_email}
              tooltip="Diese E-Mail-Adresse erscheint in Terminbriefen, Klebezetteln und Kundenkommunikation. Leer lassen, wenn sie nicht veröffentlicht werden soll."
              disabled={saving || !canManageOrgSettings}
            />
            <Input
              placeholder="Öffentliche Telefonnummer (+491701234567)"
              bind:value={form.public_contact_phone}
              tooltip="Diese Telefonnummer erscheint in Terminbriefen, Klebezetteln und Kundenkommunikation. Leer lassen, wenn sie nicht veröffentlicht werden soll."
              disabled={saving || !canManageOrgSettings}
            />
          </div>
        </div>
      </ActionBox>
    </section>

    <BrandingSection
      scope={{ type: "organization", organizationId }}
      canManage={canManageOrgSettings}
      sectionTitle="Branding"
      helpModalTextId="organizationBranding"
    />

    <ServiceHoursPreviewBox
      serviceHours={form.service_hours}
      disabled={saving || !canManageOrgSettings}
      onEdit={openServiceHoursOverlay}
    />

    {#if showChatWidget && organizationId}
      <ChatWidgetBox {organizationId} />
    {/if}

    {#if pc.isFreeTrialExperience && organizationId && canResetDemoData(pc)}
      <DemoDataSection {organizationId} disabled={saving || loading} />
    {/if}
  </div>
{/if}

<style lang="less">
  .state-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;

    p {
      margin: 0;
    }
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .settings-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .row {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }

  .row--triple {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .section-fields {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  @media (max-width: 960px) {
    .row--triple {
      grid-template-columns: 1fr;
    }
  }
</style>
